import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const TYPES = ["Boutique Hotel", "Resort & Lodge", "Wellness & Spa Club", "Holiday Let", "Restaurant", "Serviced Apartments"];

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    email: "", password: "", name: "", business_name: "", business_type: "Boutique Hotel",
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form);
      const next = searchParams.get("next");
      navigate(next || "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ink">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/14011664/pexels-photo-14011664.jpeg" alt="Hotel" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="absolute bottom-16 left-12 right-12">
          <p className="uppercase tracking-[0.3em] text-xs text-gold mb-4">Guest Relations Hub</p>
          <h2 className="text-cream text-4xl leading-tight font-serif">The digital concierge for every host.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="text-cream font-serif text-xl">Guest Relations <span className="text-gold">Hub</span></Link>
          <h1 className="text-cream text-3xl mt-10 mb-2">{isLogin ? "Welcome back" : "Create your portal"}</h1>
          <p className="text-cream-muted text-sm mb-8">
            {isLogin ? "Sign in to manage your guest relations hub." : "Launch your branded digital concierge in minutes."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label className="text-cream-muted">Your name</Label>
                  <Input data-testid="auth-name" value={form.name} onChange={upd("name")} required className="mt-1.5 bg-ink-surface border-ink text-cream" placeholder="Jane Doe" />
                </div>
                <div>
                  <Label className="text-cream-muted">Business name</Label>
                  <Input data-testid="auth-business-name" value={form.business_name} onChange={upd("business_name")} required className="mt-1.5 bg-ink-surface border-ink text-cream" placeholder="The Langford Estate" />
                </div>
                <div>
                  <Label className="text-cream-muted">Business type</Label>
                  <select data-testid="auth-business-type" value={form.business_type} onChange={upd("business_type")} className="mt-1.5 w-full h-10 rounded-md bg-ink-surface border border-ink text-cream px-3 text-sm">
                    {TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </>
            )}
            <div>
              <Label className="text-cream-muted">Email</Label>
              <Input data-testid="auth-email" type="email" value={form.email} onChange={upd("email")} required className="mt-1.5 bg-ink-surface border-ink text-cream" placeholder="you@business.com" />
            </div>
            <div>
              <Label className="text-cream-muted">Password</Label>
              <Input data-testid="auth-password" type="password" value={form.password} onChange={upd("password")} required className="mt-1.5 bg-ink-surface border-ink text-cream" placeholder="••••••••" />
            </div>
            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
            <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full rounded-full bg-gold text-black hover:bg-[#D6BB96] h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{isLogin ? "Sign in" : "Create portal"} <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </form>

          <p className="text-cream-muted text-sm mt-6 text-center">
            {isLogin ? "New to Guest Relations? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-gold hover:underline" data-testid="auth-switch">
              {isLogin ? "Create your portal" : "Sign in"}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
